import { useRef, useState } from "react";
import { Sparkles, Send, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Which users are low on credits?",
  "Summarize this week's incidents",
  "What did calls cost this month?",
];

export const AdminAssistant = () => {
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "admin", content: message }]);
    setSending(true);
    try {
      const { data } = await api.post("/admin/assistant/chat", { message, session_id: sessionId });
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: data.reply }]);
    } catch {
      toast.error("Assistant could not respond. Try again.");
    } finally {
      setSending(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
    }
  };

  const reset = () => { setMessages([]); setSessionId(crypto.randomUUID()); };

  return (
    <div className="border border-border bg-[#0A0A0A] rounded-sm p-6 space-y-4" data-testid="admin-assistant-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#007AFF]" />
          <h2 className="font-heading text-lg font-black tracking-tight">Ask Claude</h2>
        </div>
        {messages.length > 0 && (
          <Button variant="ghost" size="sm" onClick={reset} data-testid="admin-assistant-reset-btn"
            className="text-xs text-muted-foreground gap-1"><RotateCcw className="w-3 h-3" /> New chat</Button>
        )}
      </div>
      {messages.length === 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Ask anything about your users, credits, or incident reports. Claude answers from live app data.
          </p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid="admin-assistant-suggestion"
                className="text-xs border border-border rounded-sm px-3 py-1.5 text-muted-foreground hover:text-foreground hover:border-[#007AFF] transition-colors">
                {s}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-3 max-h-80 overflow-y-auto pr-1" data-testid="admin-assistant-messages">
          {messages.map((m) => (
            <div key={m.id} className={m.role === "admin" ? "flex justify-end" : "flex justify-start"}>
              <div className={`max-w-[85%] rounded-sm px-3 py-2 text-sm whitespace-pre-wrap ${
                m.role === "admin" ? "bg-[#007AFF] text-white" : "bg-background border border-border"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && <p className="text-xs text-muted-foreground animate-pulse">Claude is thinking…</p>}
          <div ref={bottomRef} />
        </div>
      )}
      <div className="flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} disabled={sending}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about users, credits, incidents…"
          data-testid="admin-assistant-input"
          className="bg-background border-border rounded-sm focus-visible:ring-[#007AFF]" />
        <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="admin-assistant-send-btn"
          className="rounded-sm bg-[#007AFF] hover:bg-[#0066d6] gap-2">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
